import clsx from 'clsx';

import { RecipientSchema } from '@/services/types/artifact-types';

interface RecipientBadgeProps {
  recipient: RecipientSchema;
  recipientRole: string;
  size?: 'sm' | 'md';
  showAvatar?: boolean;
  showRole?: boolean;
  inline?: boolean;
  primary?: boolean;
}

const getSenderInitials = (recipient: RecipientSchema) => {
  const name = recipient.name || recipient.handle || '';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getRoleColor = (role: string) => {
  // Subtle, easy-to-read design for all roles
  return 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100';
};

export const RecipientBadge = ({
  recipient,
  recipientRole: role,
  size = 'md',
  showAvatar = true,
  showRole = false,
  inline = false,
  primary = false,
}: RecipientBadgeProps) => {
  const displayName = recipient.name || recipient.handle || 'Unknown';
  const showHandle =
    recipient.name && recipient.handle && recipient.name !== recipient.handle;

  return (
    <div
      className={clsx(
        'group relative inline-flex items-center gap-2 transition-all duration-200',
        !inline && 'rounded-lg border',
        !inline && 'hover:shadow-sm focus-within:shadow-sm',
        !inline && getRoleColor(role),
        !inline && (size === 'sm' ? 'px-2 py-1' : 'px-3 py-1.5'),
      )}
      {...(!inline &&
        role !== 'from' && {
          tabIndex: 0,
          role: 'button',
          'aria-label': `${role}: ${displayName}${recipient.handle ? ` (${recipient.handle})` : ''}`,
        })}
    >
      {/* Role label */}
      {showRole && (
        <span className="text-xs text-gray-500 capitalize">{role}:</span>
      )}

      {/* Avatar */}
      {showAvatar && (
        <div
          className={clsx(
            'rounded-full bg-white/70 flex items-center justify-center font-medium text-gray-700',
            size === 'sm' ? 'h-4 w-4 text-xs' : 'h-5 w-5 text-xs',
          )}
        >
          {getSenderInitials(recipient)}
        </div>
      )}

      {/* Name and handle inline */}
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          className={clsx(
            'truncate',
            primary && 'font-semibold',
            size === 'sm' ? 'text-xs' : 'text-sm',
          )}
        >
          {displayName}
        </span>

        {showHandle && (
          <span
            className={clsx(
              'text-current/60 truncate',
              size === 'sm' ? 'text-xs' : 'text-sm',
            )}
          >
            {recipient.handle}
          </span>
        )}
      </div>

      {/* Subtle glow effect on hover */}
      {!inline && (
        <div
          className={clsx(
            'absolute inset-0 rounded-lg transition-opacity duration-200',
            'bg-gradient-to-r from-white/20 to-transparent',
            'opacity-0 group-hover:opacity-100',
          )}
        />
      )}
    </div>
  );
};
